'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { onConsentChange } from '@/lib/attribution';

function MultilineText({ text }: { text: string }) {
  const lines = text.split('\n');
  return (
    <>
      {lines.map((line, i) => (
        <React.Fragment key={i}>
          {line}
          {i < lines.length - 1 && <br />}
        </React.Fragment>
      ))}
    </>
  );
}

interface CookieRow {
  name: string;
  purpose: string;
  duration: string;
  provider: string;
  category: string;
}

export default function CookiesContent() {
  const { t } = useTranslation();

  const cookieItemsRaw = t('pageContent.cookies.sections.cookieList.items', { returnObjects: true });
  const cookieItems: CookieRow[] = Array.isArray(cookieItemsRaw) ? (cookieItemsRaw as CookieRow[]) : [];

  const handleResetConsent = () => {
    try {
      localStorage.removeItem('cookie-consent');
      // Removes the stored attribution and switches the Google tag off.
      onConsentChange('essential');
    } catch {
      // ignore — localStorage unavailable
    }
    window.location.reload();
  };

  return (
    <section className="py-16 md:py-24">
      <div className="container">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            {t('pageContent.cookies.title')}
          </h1>
          <p className="text-sm text-gray-500 mb-8">{t('pageContent.cookies.lastUpdated')}</p>

          <div className="prose prose-lg max-w-none">
            <h2>{t('pageContent.cookies.sections.intro.title')}</h2>
            <p>{t('pageContent.cookies.sections.intro.content')}</p>

            <h2>{t('pageContent.cookies.sections.whatAreCookies.title')}</h2>
            <p>{t('pageContent.cookies.sections.whatAreCookies.content')}</p>

            <h2>{t('pageContent.cookies.sections.categories.title')}</h2>
            <h3>{t('pageContent.cookies.sections.categories.essential.name')}</h3>
            <p>{t('pageContent.cookies.sections.categories.essential.description')}</p>
            <h3>{t('pageContent.cookies.sections.categories.performance.name')}</h3>
            <p>{t('pageContent.cookies.sections.categories.performance.description')}</p>
            <h3>{t('pageContent.cookies.sections.categories.marketing.name')}</h3>
            <p>{t('pageContent.cookies.sections.categories.marketing.description')}</p>

            <h2>{t('pageContent.cookies.sections.cookieList.title')}</h2>
            <p>{t('pageContent.cookies.sections.cookieList.intro')}</p>
            <div className="overflow-x-auto not-prose my-6">
              <table className="min-w-full text-sm border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 border-b border-gray-200 font-semibold text-gray-700">
                      {t('pageContent.cookies.sections.cookieList.headers.name')}
                    </th>
                    <th className="text-left px-3 py-2 border-b border-gray-200 font-semibold text-gray-700">
                      {t('pageContent.cookies.sections.cookieList.headers.purpose')}
                    </th>
                    <th className="text-left px-3 py-2 border-b border-gray-200 font-semibold text-gray-700">
                      {t('pageContent.cookies.sections.cookieList.headers.duration')}
                    </th>
                    <th className="text-left px-3 py-2 border-b border-gray-200 font-semibold text-gray-700">
                      {t('pageContent.cookies.sections.cookieList.headers.provider')}
                    </th>
                    <th className="text-left px-3 py-2 border-b border-gray-200 font-semibold text-gray-700">
                      {t('pageContent.cookies.sections.cookieList.headers.category')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {cookieItems.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2 border-b border-gray-100 font-mono text-xs">{row.name}</td>
                      <td className="px-3 py-2 border-b border-gray-100 text-gray-700">{row.purpose}</td>
                      <td className="px-3 py-2 border-b border-gray-100 text-gray-700">{row.duration}</td>
                      <td className="px-3 py-2 border-b border-gray-100 text-gray-700">{row.provider}</td>
                      <td className="px-3 py-2 border-b border-gray-100 text-gray-700">{row.category}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h2>{t('pageContent.cookies.sections.thirdPartyCookies.title')}</h2>
            <p><MultilineText text={t('pageContent.cookies.sections.thirdPartyCookies.content')} /></p>

            <h2>{t('pageContent.cookies.sections.withdrawConsent.title')}</h2>
            <p>{t('pageContent.cookies.sections.withdrawConsent.content')}</p>
            <div className="not-prose my-6">
              <Button variant="outline" onClick={handleResetConsent}>
                {t('pageContent.cookies.sections.withdrawConsent.buttonLabel')}
              </Button>
            </div>

            <h2>{t('pageContent.cookies.sections.changes.title')}</h2>
            <p>{t('pageContent.cookies.sections.changes.content')}</p>

            <h2>{t('pageContent.cookies.sections.contact.title')}</h2>
            <p>{t('pageContent.cookies.sections.contact.content')}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
